"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BookOpen, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function KnowledgeSpotlight() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="knowledge-center-highlight" data-nav="light" className="bg-background py-16 md:py-20">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} style={{
        opacity: 0
      }} className="relative overflow-hidden rounded-2xl border border-primary/20 bg-primary p-8 text-primary-foreground shadow-lg md:p-12 lg:p-14">
          {/* Subtle decorative architectural background accents */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 size-64 rounded-full bg-primary-foreground/5 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -left-20 size-80 rounded-full bg-primary-foreground/5 blur-3xl" />

          <div className="relative z-10 flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Left / Main text content */}
            <div className="max-w-2xl space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="rounded-full border-primary-foreground/30 bg-primary-foreground/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-primary-foreground uppercase">
                  Knowledge Base
                </Badge>
                <div aria-hidden="true" className="hidden items-center gap-1 text-xs text-primary-foreground/70 sm:flex">
                  <BookOpen className="size-3.5" />
                  <span>Free educational resource</span>
                </div>
              </div>

              <h2 className="font-default text-2xl font-bold tracking-tight text-primary-foreground sm:text-3xl lg:text-4xl">
                Explore our insurance and retirement guides
              </h2>

              <p className="font-default text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Get clear answers to common questions about life insurance, annuities, and retirement income strategies at your own pace. Everything is written to inform, at your own pace.
              </p>
            </div>

            {/* Right / Actions */}
            <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center lg:flex-col lg:items-stretch xl:flex-row">
              <Button asChild size="lg" className="group h-12 rounded-lg bg-primary-foreground px-6 font-semibold text-primary transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:bg-primary-foreground/90 hover:shadow-md">
                <Link to="/FAQKnowledgeCenterPage" className="inline-flex items-center justify-center gap-2">
                  <span>Visit Knowledge Center</span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 rounded-lg border-primary-foreground/30 bg-transparent px-6 font-medium text-primary-foreground transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary-foreground/60 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <HelpCircle className="size-4" />
                  <span>Ask a question</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}